<template>
    <div class="space-y-6">
        <participant-detail-section>
            {{ t('purchaseContract.customerInformation') }}
        </participant-detail-section>

        <div class="grid grid-cols-12 gap-x-4 gap-y-1 md:ml-6">
            <div
                v-if="!contractData.isGuest"
                class="col-span-12 max-md:pl-2"
            >
                <div class="flex items-center gap-2">
                    <span class="text-lg font-medium">{{
                        t('purchaseContract.notViewer', {
                            name: `${customerData.firstName} ${customerData.lastName}`
                        })
                    }}</span>
                    <prime-button
                        link
                        :label="t('purchaseContract.signInToYourProfile')"
                        @click="handleSignIntoProfile"
                    />
                </div>
                <div class="text-sm">&nbsp;</div>
            </div>

            <div
                v-if="contractData.isGuest"
                class="col-span-12 md:col-span-5"
            >
                <div class="text-sm text-gray-500">
                    {{ t('customerInformation.customerInformationSameAs') }}
                </div>
                <prime-dropdown
                    v-model="selectedEnrollmentOption"
                    :options="enrollmentOptions"
                    option-label="text"
                    class="!w-full"
                    show-clear
                    :placeholder="t('purchaseContract.selectFromParticipants')"
                />
                <div class="text-red-600 text-sm">
                    {{ invalidCustomerMessage || '&nbsp;' }}
                </div>
            </div>

            <div class="col-span-12 md:col-span-4 md:col-start-1">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>
                    {{ t('customerInformation.firstName') }}
                </div>
                <input-text
                    id="given_name"
                    :model-value="customerData.firstName"
                    autocomplete="given-name"
                    :placeholder="t('customerInformation.firstName')"
                    class="w-full"
                    :disabled="disabledCustomerInformation"
                    :pt="{
                        root: {
                            class: invalidFirstNameMessage
                                ? 'border-2 border-red-500'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @update:model-value="updateValue('firstName', $event)"
                />
                <div class="text-red-600 text-sm">
                    {{ invalidFirstNameMessage || '&nbsp;' }}
                </div>
            </div>

            <div class="col-span-12 md:col-span-4">
                <div class="text-sm text-gray-500">
                    {{ t('customerInformation.middleName') }}
                </div>
                <input-text
                    id="middle_name"
                    :model-value="customerData.middleName"
                    autocomplete="additional-name"
                    :placeholder="t('customerInformation.middleName')"
                    class="w-full"
                    :disabled="disabledCustomerInformation"
                    @update:model-value="updateValue('middleName', $event)"
                />
            </div>

            <div class="col-span-12 md:col-span-4">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>
                    {{ t('customerInformation.lastName') }}
                </div>
                <input-text
                    id="last_name"
                    :model-value="customerData.lastName"
                    autocomplete="last-name"
                    :placeholder="t('customerInformation.lastName')"
                    class="w-full"
                    :disabled="disabledCustomerInformation"
                    :pt="{
                        root: {
                            class: invalidLastNameMessage
                                ? 'border-2 border-red-500'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @update:model-value="updateValue('lastName', $event)"
                />
                <div class="text-red-600 text-sm">
                    {{ invalidLastNameMessage || '&nbsp;' }}
                </div>
            </div>

            <!-- Date of Birth -->

            <div class="col-span-12 md:col-span-5">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>
                    {{ t('customerInformation.dateOfBirth') }}
                </div>
                <dropdown-date-input
                    id="date_of_birth"
                    :date="customerData.dateOfBirth"
                    :show-errors="highlightErrors"
                    :disabled="customerData.enrollmentMatch !== null"
                    :invalid="invalidAgeMessage !== undefined"
                    @update:date="
                        payload => updateValue('dateOfBirth', payload)
                    "
                />
                <div class="text-red-600 text-sm">
                    {{ invalidAgeMessage || '&nbsp;' }}
                </div>
            </div>

            <!-- Email -->

            <div
                v-if="showEmail"
                class="col-span-12 md:col-span-5 md:col-start-1"
            >
                <div class="text-sm text-gray-500">
                    <span
                        v-if="requiredEmail"
                        class="text-red-600"
                        >*&nbsp;</span
                    >
                    {{ t('customerInformation.emailAddress') }}
                </div>
                <span class="p-input-icon-left w-full">
                    <i class="pi pi-envelope"></i>
                    <input-text
                        id="email"
                        v-model="email"
                        v-bind="emailAttrs"
                        autocomplete="email"
                        class="w-full"
                        inputmode="email"
                        type="email"
                        :placeholder="t('customerInformation.emailAddress')"
                        :pt="{
                            root: {
                                class: showInvalidEmail
                                    ? 'border-2 border-red-500'
                                    : ''
                            }
                        }"
                        :pt-options="{ mergeSections: true, mergeProps: true }"
                    />
                </span>
                <span
                    id="email-error"
                    class="text-small text-red-600"
                    >{{ showInvalidEmail || '&nbsp;' }}</span
                >
            </div>

            <div
                v-if="showEmail"
                class="col-span-12 md:col-span-5 md:col-start-1"
            >
                <div class="ml-4 flex gap-2">
                    <prime-checkbox
                        v-model="doNotMail"
                        input-id="mailing_list_checkbox"
                        :binary="true"
                    />
                    <label for="mailing_list_checkbox">
                        {{ contractData.facility.doNotMailPromptText }}
                    </label>
                </div>
                <div class="text-sm">&nbsp;</div>
            </div>

            <!-- Mobile Phone -->

            <div
                v-if="showPhone"
                class="col-span-12 md:col-span-5 md:col-start-1"
            >
                <div class="text-sm text-gray-500">
                    <span
                        v-if="requiredPhone"
                        class="text-red-600"
                        >*&nbsp;</span
                    >
                    {{ t('customerInformation.mobilePhone') }}
                </div>
                <phone-number
                    :model-value="mobilePhone"
                    :initial-value="customerData.mobilePhone ?? undefined"
                    :placeholder="t('customerInformation.mobilePhone')"
                    :autofocus="false"
                    :invalid="invalidMobilePhoneMessage !== undefined"
                    @update:model-value="updateMobilePhone"
                />
                <div class="text-red-600 text-sm">
                    {{ invalidMobilePhoneMessage || '&nbsp;' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export type CustomerData = {
    enrollmentMatch: null | {
        enrollmentTypeId: string
        enrollmentIndex: number
    }
    firstName: string
    middleName: string
    lastName: string
    dateOfBirth: string
    email: string
    doNotMail: boolean | null
    // null represents invalid phone number
    mobilePhone: string | null
    invalid: boolean
}

export const CustomerInputPlanContractFragment = graphql(/* GraphQL */ `
    fragment CustomerInputPlanContractFragment on PlanContract {
        plan {
            terms {
                contractHolderMinimumAge
                askForEmailForGuestAtKiosk
                optionalEmailForGuestAtKiosk
                askForPhoneForGuestAtKiosk
                requiredPhoneForGuestAtKiosk
                requirePhoneNumber
            }
        }
        facility {
            doNotMailDirection
            doNotMailDefaultValue
            doNotMailPromptText(language: $language)
        }
        isGuest
    }
`)
</script>

<script setup lang="ts">
import InputText from 'primevue/inputtext'
import ParticipantDetailSection from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetailSection.vue'
import PhoneNumber, {
    type PhoneObject
} from '~/components/Input/PhoneNumber.vue'
import DropdownDateInput from '~/components/Input/DropdownDateInput.vue'
import PrimeButton from 'primevue/button'
import PrimeDropdown from 'primevue/dropdown'
import type {
    EnrollmentInputArray,
    EnrollmentInputExistingEnrollment,
    EnrollmentInputNewEnrollment
} from '~/composables/enrollment-input'
import { computed, onMounted, ref, watch } from 'vue'
import { useShowLoginModal, useViewer } from '~/composables/use-login'
import PrimeCheckbox from 'primevue/checkbox'
import { type FragmentType, getFragmentData } from '~/resources/graphql'
import {
    doNotMailChecked,
    doNotMailValue
} from '~/components/PurchaseContract/do-not-mail-helpers'
import { now, parseTimeless } from '~/composables/support/localization'
import { differenceInYears } from 'date-fns'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useI18n } from 'vue-i18n'
import { useLayoutMode } from '~/composables/use-layout-mode'

const props = defineProps<{
    contract: FragmentType<typeof CustomerInputPlanContractFragment>
    customerData: CustomerData
    enrollmentInputs: EnrollmentInputArray
    highlightErrors: boolean
}>()

const emit = defineEmits<{
    (e: 'update:customerData', newData: CustomerData): void
}>()

onMounted(() => {
    email.value = props.customerData.email
    // Ensure that invalid is set to the correct value
    updateCustomerData(props.customerData)
})

const { isKiosk } = useLayoutMode()
const { t } = useI18n()
const contractData = computed(() =>
    getFragmentData(CustomerInputPlanContractFragment, props.contract)
)

const showEmail = computed(
    () =>
        !isKiosk.value ||
        !contractData.value.isGuest ||
        contractData.value.plan.terms.askForEmailForGuestAtKiosk
)

const requiredEmail = computed(
    () =>
        !contractData.value.isGuest ||
        (contractData.value.plan.terms.askForEmailForGuestAtKiosk &&
            !contractData.value.plan.terms.optionalEmailForGuestAtKiosk)
)

const showPhone = computed(
    () =>
        !isKiosk.value ||
        !contractData.value.isGuest ||
        contractData.value.plan.terms.askForPhoneForGuestAtKiosk ||
        contractData.value.plan.terms.requirePhoneNumber
)

const requiredPhone = computed(
    () =>
        (contractData.value.isGuest &&
            contractData.value.plan.terms.askForPhoneForGuestAtKiosk &&
            contractData.value.plan.terms.requiredPhoneForGuestAtKiosk) ||
        contractData.value.plan.terms.requirePhoneNumber
)

const invalidFirstNameMessage = computed(() => {
    if (props.highlightErrors && props.customerData.firstName.length < 1) {
        return t('customerInformation.firstNameRequired')
    } else {
        return undefined
    }
})

const invalidLastNameMessage = computed(() => {
    if (props.highlightErrors && props.customerData.lastName.length < 2) {
        return t('customerInformation.twoOrMoreCharactersRequired')
    } else {
        return undefined
    }
})

function ageLessThanMinimum(dateOfBirth: string) {
    if (contractData.value.plan.terms.contractHolderMinimumAge === null) {
        return false
    }

    const customerDob = parseTimeless(dateOfBirth)
    const customerAge = differenceInYears(
        now(activeFacility.value.timezone),
        customerDob
    )

    return customerAge < contractData.value.plan.terms.contractHolderMinimumAge
}

const invalidAgeMessage = computed(() => {
    if (props.highlightErrors === false) {
        return undefined
    }

    // The date of birth component will show errors for us in this case
    if (props.customerData.dateOfBirth === '') {
        return undefined
    }

    if (ageLessThanMinimum(props.customerData.dateOfBirth)) {
        return t('purchaseContract.ageAtLeastRequired', {
            minimum: contractData.value.plan.terms.contractHolderMinimumAge
        })
    }
    return undefined
})

const { errors, defineField } = useForm({
    validationSchema: object({
        email: string()
            .email(t('customerInformation.invalidEmailWarning'))
            .test({
                message: t('customerInformation.emailRequiredWarning'),
                test: value => {
                    if (requiredEmail.value) {
                        return typeof value === 'string' && value.length > 0
                    }

                    return true
                }
            })
    }),
    validateOnMount: true
})

const [email, emailAttrs] = defineField('email', {
    validateOnModelUpdate: false,
    validateOnBlur: true
})

// Validate on blur so this code runs on blur if there are changes
watch(errors, newValue => {
    // If we have errors we emit empty string which will be invalid
    if (newValue.email) {
        updateValue('email', '')
    } else {
        updateValue('email', email.value)
    }
})

const showInvalidEmail = computed(
    () => props.highlightErrors && errors.value.email
)

const mobilePhone = ref<PhoneObject | undefined>()
function updateMobilePhone(value: PhoneObject | undefined) {
    mobilePhone.value = value
    // If we have a valid phone number, we emit that
    if (value !== undefined && value.valid === true) {
        updateValue('mobilePhone', value.number)
    }
    // Else if our value is undefined emit empty
    else if (value === undefined || value.valid === undefined) {
        updateValue('mobilePhone', '')
    }
    // Else we have an invalid value
    else {
        updateValue('mobilePhone', null)
    }
}

const invalidMobilePhoneMessage = computed(() => {
    // No showing errors yet
    if (!props.highlightErrors) {
        return undefined
    }

    // If required, we must have a valid number
    if (
        requiredPhone.value &&
        (mobilePhone.value === undefined ||
            mobilePhone.value.number === undefined ||
            !mobilePhone.value.valid)
    ) {
        return t('customerInformation.invalidMobilePhoneWarning')
    }

    // Otherwise, only check validity if we have a number
    if (
        mobilePhone.value !== undefined &&
        mobilePhone.value.number !== undefined &&
        !mobilePhone.value.valid
    ) {
        return t('customerInformation.invalidMobilePhoneWarning')
    } else {
        return undefined
    }
})

const activeFacility = useActiveFacility()
function updateValue<T extends keyof CustomerData>(
    key: T,
    value: CustomerData[T]
) {
    updateCustomerData({
        ...props.customerData,
        [key]: value
    })
}

function updateCustomerData(value: CustomerData) {
    emit('update:customerData', {
        ...value,
        invalid: customerDataIsInvalid(value)
    })
}

function customerDataIsInvalid(data: CustomerData) {
    return (
        data.firstName.length < 1 ||
        data.lastName.length < 2 ||
        data.dateOfBirth === '' ||
        ageLessThanMinimum(data.dateOfBirth) ||
        (showEmail.value && requiredEmail.value && data.email === '') ||
        data.mobilePhone === null ||
        (showPhone.value && requiredPhone.value && data.mobilePhone === '') ||
        (contractData.value.isGuest && invalidCustomerMessage.value !== '')
    )
}

const doNotMail = computed({
    get() {
        return doNotMailChecked(
            contractData.value.facility.doNotMailDirection,
            props.customerData.doNotMail
        )
    },
    set(newValue) {
        updateValue(
            'doNotMail',
            doNotMailValue(
                contractData.value.facility.doNotMailDirection,
                newValue
            )
        )
    }
})

const selectedEnrollmentOption = ref<{
    text: string
    enrollment: EnrollmentInputNewEnrollment | EnrollmentInputExistingEnrollment
    match: { enrollmentTypeId: string; enrollmentIndex: number }
} | null>(null)

const enrollmentOptions = computed(() => {
    return props.enrollmentInputs.flatMap(enrollmentInput => {
        return enrollmentInput.enrollments
            .map((enrollment, enrollmentIndex) => {
                if (enrollment.data === null) {
                    return null
                } else if (enrollment.data?.type === 'new') {
                    if (
                        enrollment.data.firstName === '' ||
                        enrollment.data.lastName === ''
                    ) {
                        return null
                    }
                    return {
                        text:
                            enrollment.data.lastName +
                            ', ' +
                            enrollment.data.firstName,
                        enrollment: enrollment.data,
                        match: {
                            enrollmentTypeId: enrollmentInput.enrollmentType.id,
                            enrollmentIndex
                        }
                    }
                } else if (enrollment.data?.type === 'existing') {
                    return {
                        text: enrollment.data.commaName,
                        enrollment: enrollment.data,
                        match: {
                            enrollmentTypeId: enrollmentInput.enrollmentType.id,
                            enrollmentIndex
                        }
                    }
                } else {
                    throw new Error()
                }
            })
            .filter(value => value !== null)
    })
})

watch(
    () => props.customerData.enrollmentMatch,
    newValue => {
        if (newValue !== null) {
            const matchingOption = enrollmentOptions.value.find(
                option =>
                    option !== null &&
                    option.match.enrollmentTypeId ===
                        newValue.enrollmentTypeId &&
                    option.match.enrollmentIndex === newValue.enrollmentIndex
            )

            // The enrollment no longer exists, so we should wipe the data we have
            if (matchingOption === undefined) {
                selectedEnrollmentOption.value = null
            }
            // Otherwise, select the enrollment again
            else {
                selectedEnrollmentOption.value = matchingOption
            }
        }
    },
    { immediate: true }
)

watch(enrollmentOptions, newValue => {
    const selectedValue = selectedEnrollmentOption.value
    if (selectedValue !== null) {
        const foundValue = newValue.find(option => {
            return (
                option !== null &&
                option.match.enrollmentTypeId ===
                    selectedValue.match.enrollmentTypeId &&
                option.match.enrollmentIndex ===
                    selectedValue.match.enrollmentIndex
            )
        })

        if (foundValue) {
            selectedEnrollmentOption.value = foundValue
        }
    }
})

watch(selectedEnrollmentOption, newValue => {
    if (newValue === null) {
        updateCustomerData({
            ...props.customerData,
            enrollmentMatch: null,
            firstName: '',
            middleName: '',
            lastName: '',
            dateOfBirth: ''
        })
    } else {
        updateCustomerData({
            ...props.customerData,
            enrollmentMatch: newValue.match,
            firstName: newValue.enrollment.firstName,
            middleName: newValue.enrollment.middleName,
            lastName: newValue.enrollment.lastName,
            dateOfBirth: newValue.enrollment.dateOfBirth
        })
    }
})

const disabledCustomerInformation = computed(() => {
    return (
        props.customerData.enrollmentMatch !== null ||
        !contractData.value.isGuest
    )
})

const invalidCustomerMessage = computed(() => {
    if (enrollmentOptions.value.length === 0) {
        return ''
    }

    // Find contract holder required enrollment type
    const hasRequireContractHolder = props.enrollmentInputs.find(input => {
        if (input.enrollmentType.requireContractHolder) {
            return true
        }
    })

    // No requirement, nothing to do
    if (hasRequireContractHolder === undefined) {
        return ''
    }

    // No selected customer enrollment
    if (selectedEnrollmentOption.value === null) {
        return t('customerInformation.customerMustBeTheEnrollmentType', {
            enrollmentType: hasRequireContractHolder.enrollmentType.singular
        })
    }

    if (
        selectedEnrollmentOption.value.match.enrollmentTypeId !==
        hasRequireContractHolder.enrollmentType.id
    ) {
        return t('customerInformation.customerMustBeTheEnrollmentType', {
            enrollmentType: hasRequireContractHolder.enrollmentType.singular
        })
    }

    return ''
})

const ageOfMajorityHint = computed(() => {
    const contractHolderType = props.enrollmentInputs.find(
        input =>
            input.enrollmentType.requireContractHolder &&
            input.enrollmentType.ageEnforcementMode === 'ENFORCE_IN_CPX'
    )

    if (contractHolderType !== undefined) {
        return contractHolderType.enrollmentType.ageMinimum
    }

    return 0
})

const viewer = useViewer()
const showLoginModal = useShowLoginModal()
async function handleSignIntoProfile() {
    const currentViewerId = viewer.value?.customerId
    const response = await showLoginModal({
        ageOfMajorityHint: ageOfMajorityHint.value
    })
    if (
        response !== undefined &&
        currentViewerId !== undefined &&
        currentViewerId !== response
    ) {
        location.reload()
    }
}
</script>
